// Single source-of-truth for the integration API documentation shown in the
// admin UI. When adding a new endpoint in the Go handler, add a matching entry
// here so the in-page docs stay honest. The routes themselves are registered in
// one place too: handlers.RegisterIntegrationRoutes.

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface EndpointParam {
  name: string;
  in: 'path' | 'query';
  required?: boolean;
  description: string;
  example?: string;
}

export interface EndpointDoc {
  id: string;
  group: 'Servers' | 'Health' | 'Proxies' | 'Networks' | 'Meta' | 'Reports' | 'By your id';
  method: HttpMethod;
  path: string;
  summary: string;
  /** Short description, rendered as plain paragraph above params. */
  description?: string;
  permission: 'integration:read' | 'integration:write';
  params?: EndpointParam[];
  /** Example request body, rendered as a formatted JSON snippet. */
  requestExample?: unknown;
  /** Example successful response payload (inside the `data` envelope field). */
  responseExample?: unknown;
  /** Optional status code shown in the success pill (default 200). */
  successStatus?: number;
  /** Relevant non-success status codes and what they mean. */
  errorStatuses?: { code: number; description: string }[];
}

// Short aliases for the most common error sets, to avoid repetition.
const NOT_FOUND = { code: 404, description: 'Not found, or hidden by your source filter.' };
const AUTH_ERRORS = [
  { code: 401, description: 'Missing or disabled API key.' },
  { code: 403, description: 'Missing required permission.' },
  { code: 429, description: 'Rate limit exceeded (check Retry-After).' },
];
const WRITE_ERRORS = [
  { code: 400, description: 'Validation error (bad IP, meta too large, invalid protocol, ...).' },
  ...AUTH_ERRORS,
  NOT_FOUND,
];
const KEY_PARAM: EndpointParam = { name: 'key', in: 'path', required: true, description: 'Server key', example: 'srv-abc' };

// How a proxy list or entry sent by a client is stored — the same rules for the
// server PUT and the proxy PUT.
const PROXY_MERGE_RULES =
  'Entries are matched to the stored ones by host:port. For a matched endpoint the stored password is kept when ' +
  'you send the same login without one (responses never include passwords, so a GET → modify → PUT round trip ' +
  'would otherwise strip them); a different login does not inherit it, and no login removes auth. The learned ' +
  'country is kept when you send none, and preferredTarget is always the system\'s value.';

const serverExample = {
  id: '65f0a1b2c3d4e5f6a7b8c9d1',
  key: 'srv-abc',
  name: 'edge-de-01',
  mainIP: '1.2.3.4',
  networks: ['192.168.1.0/24'],
  proxies: [
    { protocol: 'socks5', host: '1.2.3.4', port: 1080, login: 'user', hasPassword: true },
    {
      protocol: 'http', host: '1.2.3.4', port: 8080, hasPassword: false, label: 'modem-3',
      country: 'RO', preferredTarget: 'https://www.baidu.com',
    },
  ],
  checkMode: 'active-probing',
  type: 'dedicated',
  country: 'DE',
  tags: ['production'],
  enabled: true,
  priority: 0,
  sourceKey: 'sysboard',
  sourceId: 'sb-42',
  meta: { owner: 'team-a', inventoryId: 'inv-9001' },
  proxyMetas: { '1.2.3.4:1080': { vendor: 'Squid' } },
  createdAt: '2026-09-22T10:12:33Z',
  updatedAt: '2026-09-23T08:40:02Z',
};

const failuresExample = {
  reportId: '65f0a1b2c3d4e5f6a7b8c9d0',
  generatedAt: '2026-09-23T10:00:00Z',
  items: [
    {
      serverKey: 'srv-abc',
      serverName: 'edge-de-01',
      mainIP: '1.2.3.4',
      country: 'DE',
      sourceKey: 'sysboard',
      sourceId: 'sb-42',
      meta: { owner: 'team-a' },
      status: 'ZOMBIE',
      failures: [
        {
          type: 'proxy', id: '1.2.3.4:8080', protocol: 'http', label: 'modem-3', host: '1.2.3.4', port: 8080,
          state: 'ZOMBIE', confirmed: true, checkedAt: '2026-09-23T09:58:41Z',
          failedStep: 'proxy_request',
          error: 'all 1 targets failed — https://www.google.com: content_mismatch: expected content not found in response body; ' +
            'not attempted: https://www.baidu.com: disabled: no worker in the fleet can reach this target',
          latency: 412,
          meta: { modem: 'SIM-7' },
          workerName: 'Local Checker', workerIP: '203.0.113.7', workerCountry: 'DE',
          steps: [
            { name: 'proxy_connect', success: true, latency: 38 },
            {
              name: 'proxy_request', success: false, latency: 412, error: 'all 1 targets failed',
              notes: ['1 target(s) disabled fleet-wide and not asked: https://www.baidu.com'],
            },
            { name: 'proxy_latency', success: false, latency: 0, error: 'skipped: proxy request failed' },
          ],
          targets: [
            {
              url: 'https://www.google.com', success: false, latency: 412,
              error: 'content_mismatch: expected content not found in response body', contentMismatch: true,
            },
            {
              url: 'https://www.baidu.com', success: false, latency: 0,
              error: 'disabled: no worker in the fleet can reach this target', skipped: true, skipReason: 'disabled_fleet_wide',
            },
          ],
          statusTimeline: [
            { state: 'HEALTHY', at: '2026-09-23T07:12:00Z' },
            { state: 'ZOMBIE', at: '2026-09-23T09:41:10Z' },
          ],
        },
        {
          type: 'proxy', id: '1.2.3.4:1080', protocol: 'socks5', host: '1.2.3.4', port: 1080,
          state: 'SUSPECT', confirmed: false, checkedAt: '2026-09-23T09:59:02Z',
          failedStep: 'proxy_connect', error: 'dial tcp 1.2.3.4:1080: connect: connection refused', latency: 1,
          meta: {},
          steps: [{ name: 'proxy_connect', success: false, latency: 1, error: 'dial tcp 1.2.3.4:1080: connect: connection refused' }],
        },
        {
          type: 'ip', id: '10.0.2.7', state: 'DOWN', confirmed: true, recovering: true,
          checkedAt: '2026-09-23T09:57:30Z', staleSince: '2026-09-23T06:05:00Z', latency: 21, meta: {},
          steps: [{ name: 'icmp_ping', success: true, latency: 21 }],
        },
      ],
    },
  ],
  limit: 100,
  skip: 0,
  hasMore: false,
};

const healthExample = {
  serverKey: 'srv-abc',
  serverName: 'edge-de-01',
  sourceKey: 'sysboard',
  sourceId: 'sb-42',
  type: 'dedicated',
  enabled: true,
  status: 'DEGRADED',
  summary: { total: 4, healthy: 2, failing: 1, suspect: 0, unknown: 1 },
  mainIP: {
    type: 'ip', id: '1.2.3.4', state: 'HEALTHY', confirmed: true, checkedAt: '2026-09-23T09:58:10Z',
    latency: 12.4, packetLoss: 0,
    steps: [{ name: 'icmp_ping', success: true, latency: 12.4 }],
    statusTimeline: [{ state: 'HEALTHY', at: '2026-09-20T07:00:00Z' }],
    workerName: 'de-1', workerIP: '203.0.113.7', workerCountry: 'DE',
  },
  networks: [
    { type: 'ip', id: '10.0.2.1', network: '10.0.2.0/24', state: 'UNKNOWN', confirmed: false, latency: 0 },
  ],
  proxies: [
    {
      type: 'proxy', id: '1.2.3.4:8080', protocol: 'http', label: 'modem-3', host: '1.2.3.4', port: 8080,
      state: 'ZOMBIE', confirmed: true, checkedAt: '2026-09-23T09:58:41Z', latency: 412,
      failedStep: 'proxy_request', error: 'all 1 targets failed',
      steps: [
        { name: 'proxy_connect', success: true, latency: 38 },
        { name: 'proxy_request', success: false, latency: 412, error: 'all 1 targets failed' },
      ],
      targets: [{
        url: 'https://www.google.com', success: false, latency: 412,
        error: 'content_mismatch: expected content not found in response body', contentMismatch: true,
      }],
    },
    {
      type: 'proxy', id: '1.2.3.4:1080', protocol: 'socks5', host: '1.2.3.4', port: 1080,
      state: 'HEALTHY', confirmed: true, checkedAt: '2026-09-23T09:58:44Z', latency: 380,
      steps: [
        { name: 'proxy_connect', success: true, latency: 35 },
        { name: 'proxy_request', success: true, latency: 380 },
        { name: 'proxy_latency', success: true, latency: 190, notes: ['measured against www.google.com:443'] },
      ],
      targets: [{ url: 'https://www.google.com', success: true, latency: 380, contentValidated: true }],
    },
  ],
};

const KEYED_ENDPOINTS: EndpointDoc[] = [
  // ── Servers ─────────────────────────────────────────────────────────────
  {
    id: 'servers-list',
    group: 'Servers',
    method: 'GET',
    path: '/servers',
    summary: 'List servers visible to the token',
    description:
      'Paginated with skip + limit. No total count is returned — iterate until hasMore is false. ' +
      'If the token has a source filter, only matching servers appear. Proxy entries never carry a password ' +
      '(hasPassword says one is stored); country and preferredTarget on a proxy are filled in by the system. ' +
      'Per-proxy meta is in proxyMetas, keyed by host:port.',
    permission: 'integration:read',
    params: [
      { name: 'skip', in: 'query', description: 'Number of items to skip. Default 0.', example: '0' },
      { name: 'limit', in: 'query', description: 'Page size. Default 100, max 500.', example: '100' },
    ],
    responseExample: {
      items: [serverExample],
      limit: 100,
      skip: 0,
      hasMore: false,
    },
    errorStatuses: AUTH_ERRORS,
  },
  {
    id: 'servers-get',
    group: 'Servers',
    method: 'GET',
    path: '/servers/{key}',
    summary: 'Fetch a single server by key',
    description:
      'Returns 404 both when the server does not exist and when the token is filtered to a different source ' +
      '— we intentionally do not leak existence. Also available by your own id: /servers/by-source/{sourceId}.',
    permission: 'integration:read',
    params: [KEY_PARAM],
    responseExample: serverExample,
    errorStatuses: [...AUTH_ERRORS, NOT_FOUND],
  },
  {
    id: 'servers-create',
    group: 'Servers',
    method: 'POST',
    path: '/servers',
    summary: 'Create a server',
    description:
      'If the token is source-filtered, the body sourceKey is ignored and overwritten with the token source. ' +
      'A sourceId must be unique within its source (409 otherwise — a retried create cannot leave two servers ' +
      'behind one id). A client-chosen key may not be "by-source" or contain "/", "?" or "#". ' +
      'preferredTarget on a proxy is ignored.',
    permission: 'integration:write',
    requestExample: {
      name: 'edge-de-01',
      mainIP: '1.2.3.4',
      networks: ['192.168.1.0/24'],
      proxies: [{ protocol: 'socks5', host: '1.2.3.4', port: 1080, login: 'user', password: 'pass' }],
      type: 'dedicated',
      sourceId: 'sb-42',
      meta: { inventoryId: 'inv-9001' },
    },
    responseExample: serverExample,
    successStatus: 201,
    errorStatuses: [
      { code: 400, description: 'Validation error (bad IP, meta too large, bad protocol, reserved key, ...).' },
      { code: 409, description: 'Another server of this source already has this sourceId.' },
      ...AUTH_ERRORS,
    ],
  },
  {
    id: 'servers-update',
    group: 'Servers',
    method: 'PUT',
    path: '/servers/{key}',
    summary: 'Partial update',
    description:
      'Any field you omit stays untouched. Source-filtered tokens cannot change sourceKey/sourceId/sourceUrl. ' +
      'proxies, when sent, is the complete new list — endpoints you leave out are removed. ' + PROXY_MERGE_RULES + ' ' +
      'Endpoints and networks the update removes stop appearing in reports immediately.',
    permission: 'integration:write',
    params: [KEY_PARAM],
    requestExample: {
      enabled: false,
      tags: ['retired'],
      proxies: [{ protocol: 'socks5', host: '1.2.3.4', port: 1080, login: 'user', label: 'renamed' }],
    },
    responseExample: serverExample,
    errorStatuses: [
      ...WRITE_ERRORS,
      { code: 409, description: 'The new sourceKey/sourceId pair is already used by another server.' },
    ],
  },
  {
    id: 'servers-delete',
    group: 'Servers',
    method: 'DELETE',
    path: '/servers/{key}',
    summary: 'Hard delete',
    description:
      'Removes the server and all associated data: health records, history, check logs, ticket references. ' +
      'Checks already in flight are discarded when they complete, so the server does not reappear in the next report. ' +
      'Historical reports keep describing it — they are frozen records.',
    permission: 'integration:write',
    params: [KEY_PARAM],
    responseExample: { deleted: true },
    errorStatuses: [...AUTH_ERRORS, NOT_FOUND],
  },

  // ── Health ──────────────────────────────────────────────────────────────
  {
    id: 'servers-health',
    group: 'Health',
    method: 'GET',
    path: '/servers/{key}/health',
    summary: 'Current health of one server — every target, working or not',
    description:
      'The same picture as the server\'s health page in the panel: the main IP, every network and every proxy, as of ' +
      'each one\'s latest scheduled check. No check history. Target fields mean what they mean on /reports/{id}/failures ' +
      'items (state, confirmed, recovering, checkedAt, staleSince, steps, targets, statusTimeline, worker); health items ' +
      'carry no meta. UNKNOWN = no result yet (confirmed is false then). A CIDR network is checked through one address ' +
      'of it: network is the entry as configured, id the address. status follows the panel: the main IP decides; if it ' +
      'is HEALTHY, a DEGRADED/ZOMBIE/ERROR/DOWN/SUSPECT target elsewhere makes the server DEGRADED; STALE and UNKNOWN ' +
      'targets do not count (mobile gateways weigh their modems instead). summary.failing counts DEGRADED, ZOMBIE, ' +
      'ERROR, DOWN and STALE.',
    permission: 'integration:read',
    params: [KEY_PARAM],
    responseExample: healthExample,
    errorStatuses: [...AUTH_ERRORS, NOT_FOUND],
  },

  // ── Proxies ─────────────────────────────────────────────────────────────
  {
    id: 'proxies-add',
    group: 'Proxies',
    method: 'POST',
    path: '/servers/{key}/proxies',
    summary: 'Append a proxy',
    description:
      'Returns 409 if a proxy with the same host:port already exists on this server. Inline meta is stored in the ' +
      'server\'s proxyMetas under host:port. preferredTarget is ignored.',
    permission: 'integration:write',
    params: [KEY_PARAM],
    requestExample: {
      protocol: 'http',
      host: '1.2.3.4',
      port: 8080,
      login: 'user',
      password: 'pass',
      label: 'Orange SIM',
      meta: { vendor: 'Squid' },
    },
    responseExample: {
      protocol: 'http',
      host: '1.2.3.4',
      port: 8080,
      login: 'user',
      hasPassword: true,
      label: 'Orange SIM',
    },
    successStatus: 201,
    errorStatuses: [
      { code: 400, description: 'Validation error.' },
      { code: 409, description: 'Proxy with same host:port already exists.' },
      ...AUTH_ERRORS,
      NOT_FOUND,
    ],
  },
  {
    id: 'proxies-update',
    group: 'Proxies',
    method: 'PUT',
    path: '/servers/{key}/proxies/{proxyId}',
    summary: 'Replace a proxy by current host:port',
    description:
      'proxyId is the current host:port. The body may change host/port; we fail with 409 if the new pair collides. ' +
      'Send the same login without a password to keep the stored one — also when the address changes. A changed ' +
      'address is a different endpoint: the learned country and preferredTarget start over and the old address\'s ' +
      'check data is purged.',
    permission: 'integration:write',
    params: [
      KEY_PARAM,
      { name: 'proxyId', in: 'path', required: true, description: 'Current host:port', example: '1.2.3.4:1080' },
    ],
    requestExample: { protocol: 'socks5', host: '1.2.3.4', port: 1080, login: 'user', label: 'renamed' },
    responseExample: { protocol: 'socks5', host: '1.2.3.4', port: 1080, login: 'user', hasPassword: true, label: 'renamed' },
    errorStatuses: [
      { code: 400, description: 'Validation error or invalid proxyId format.' },
      { code: 409, description: 'New host:port conflicts with another proxy on this server.' },
      ...AUTH_ERRORS,
      NOT_FOUND,
    ],
  },
  {
    id: 'proxies-delete',
    group: 'Proxies',
    method: 'DELETE',
    path: '/servers/{key}/proxies/{proxyId}',
    summary: 'Remove a proxy',
    description:
      'Also purges the proxy\'s stored check data (health, history, flap counters, egress history). A check already ' +
      'in flight is discarded, so the endpoint does not reappear in the next report and, if re-added, starts clean.',
    permission: 'integration:write',
    params: [
      KEY_PARAM,
      { name: 'proxyId', in: 'path', required: true, description: 'host:port', example: '1.2.3.4:1080' },
    ],
    responseExample: { deleted: true },
    errorStatuses: [...AUTH_ERRORS, NOT_FOUND],
  },
  {
    id: 'proxies-meta-patch',
    group: 'Meta',
    method: 'PATCH',
    path: '/servers/{key}/proxies/{proxyId}/meta',
    summary: 'Merge-patch proxy meta',
    description:
      'Top-level keys with non-null values overwrite existing entries. Keys with null values are deleted. ' +
      'Meta size is capped at 4 KB after merge. The proxy must exist in proxies first.',
    permission: 'integration:write',
    params: [
      KEY_PARAM,
      { name: 'proxyId', in: 'path', required: true, description: 'host:port', example: '1.2.3.4:1080' },
    ],
    requestExample: { egressAsn: 42, legacyTag: null },
    responseExample: { id: '1.2.3.4:1080', meta: { egressAsn: 42, vendor: 'Squid' } },
    errorStatuses: [
      { code: 400, description: 'Meta exceeds 4 KB or body is not a JSON object.' },
      ...AUTH_ERRORS,
      NOT_FOUND,
    ],
  },

  // ── Networks ────────────────────────────────────────────────────────────
  {
    id: 'networks-replace',
    group: 'Networks',
    method: 'PUT',
    path: '/servers/{key}/networks',
    summary: 'Replace the entire networks list',
    description:
      'Networks are small (a handful per server). Whole-array replace keeps the API simple for callers who already ' +
      'have the full set. Networks you drop stop appearing in reports immediately. A CIDR entry is checked through ' +
      'one address inside it.',
    permission: 'integration:write',
    params: [KEY_PARAM],
    requestExample: { networks: ['192.168.1.0/24', '10.0.2.0/24'] },
    responseExample: { networks: ['192.168.1.0/24', '10.0.2.0/24'] },
    errorStatuses: WRITE_ERRORS,
  },

  // ── Server-level meta ───────────────────────────────────────────────────
  {
    id: 'server-meta-patch',
    group: 'Meta',
    method: 'PATCH',
    path: '/servers/{key}/meta',
    summary: 'Merge-patch server meta',
    description:
      'Top-level keys with non-null values overwrite; null values delete. Max 16 KB after merge.',
    permission: 'integration:write',
    params: [KEY_PARAM],
    requestExample: { owner: 'team-b', legacyField: null },
    responseExample: { meta: { owner: 'team-b', inventoryId: 'inv-9001' } },
    errorStatuses: [
      { code: 400, description: 'Meta exceeds 16 KB or body is not a JSON object.' },
      ...AUTH_ERRORS,
      NOT_FOUND,
    ],
  },

  // ── Reports ─────────────────────────────────────────────────────────────
  {
    id: 'reports-latest',
    group: 'Reports',
    method: 'GET',
    path: '/reports/latest',
    summary: 'Latest report — id and timestamp',
    description:
      'Slim poll-signal endpoint: tells you whether a new report exists. Compare the returned id or generatedAt with what you saw last ' +
      'and, if newer, call /reports/{id}/failures for the details. Served from a Redis cache (TTL 6h, refreshed on each new report).',
    permission: 'integration:read',
    responseExample: {
      id: '65f0a1b2c3d4e5f6a7b8c9d0',
      date: '2026-09-23 10:00',
      generatedAt: '2026-09-23T10:00:00Z',
    },
    errorStatuses: [...AUTH_ERRORS, { code: 404, description: 'No reports generated yet.' }],
  },
  {
    id: 'reports-failures',
    group: 'Reports',
    method: 'GET',
    path: '/reports/{id}/failures',
    summary: 'What broke — servers with failing targets in the given report',
    description:
      'Only failures are returned, never the full inventory. A report is a frozen record: every item was copied into ' +
      'the report when it was generated, as values and text — changing or deleting targets, proxies or servers later ' +
      'does not change it (only meta is read live). ' +
      'States: DOWN / ERROR / ZOMBIE / DEGRADED / STALE are confirmed failures; SUSPECT items (failing, not yet confirmed: ' +
      '3 fails in a row, 5 for mobile-gateway proxies) are listed with confirmed:false, so a target missing from the ' +
      'list is not failing — pass confirmedOnly=true to leave them out. For a proxy: DOWN = no TCP connection; ERROR = ' +
      'connected but no request succeeded (407, bad status, reset, or every attempt timed out); ZOMBIE = it answered ' +
      'with something other than the target (a stub page); DEGRADED = tunnel handshake failed or too slow. ' +
      'Per item: checkedAt (when the check ran — can be older than the report), staleSince (start of the current ' +
      'DOWN/ERROR streak), recovering (still failing by state, but the latest check passed), steps (each with severity ' +
      'and text notes, e.g. a worker that could not reach any target), targets (every URL attempted; contentMismatch = ' +
      'stub page; skipped + skipReason = never asked, not a failure), statusTimeline (becoming healthy, then each ' +
      'escalation — the last entry is the worst state reached, not necessarily the current one). ' +
      'Identify proxy endpoints by host + port rather than parsing id. error is a human-readable summary, not a contract. ' +
      'items[].status is the worst listed state: DOWN > ERROR > ZOMBIE > DEGRADED > STALE > SUSPECT.',
    permission: 'integration:read',
    params: [
      { name: 'id', in: 'path', required: true, description: 'Report ID' },
      { name: 'skip', in: 'query', description: 'Servers to skip. Default 0' },
      { name: 'limit', in: 'query', description: 'Page size. Default 100, max 500' },
      { name: 'confirmedOnly', in: 'query', description: 'true = leave out SUSPECT (unconfirmed) items', example: 'true' },
    ],
    responseExample: failuresExample,
    errorStatuses: [...AUTH_ERRORS, NOT_FOUND],
  },
];

// Every endpoint addressing one server by our key also exists under
// /servers/by-source/{sourceId}, the integrator's own id — generated from the
// keyed entries so the two lists cannot drift apart (the backend registers them
// the same way).
const SOURCE_ID_PARAMS: EndpointParam[] = [
  { name: 'sourceId', in: 'path', required: true, description: 'Your own id for the server', example: 'sb-42' },
  {
    name: 'sourceKey',
    in: 'query',
    description: 'Required only when the API key is not bound to a source (sourceId is unique within one source). ' +
      'A bound key uses its own; naming another source gives 404.',
    example: 'sysboard',
  },
];

const bySource = (e: EndpointDoc): EndpointDoc => ({
  ...e,
  id: `${e.id}-by-source`,
  group: 'By your id',
  path: e.path.replace('/servers/{key}', '/servers/by-source/{sourceId}'),
  summary: `${e.summary.replace(/ by key$/, "")} — by your id`,
  description:
    `Same as ${e.method} ${e.path}, addressed by the id you gave the server (sourceId). ` +
    'sourceIds containing "/" cannot be used here — address those servers by key.',
  params: [...SOURCE_ID_PARAMS, ...(e.params || []).filter((p) => p.name !== 'key')],
  errorStatuses: [
    { code: 400, description: 'Key not bound to a source and no sourceKey given.' },
    ...(e.errorStatuses || []).filter((s) => s.code !== 400),
  ],
});

export const INTEGRATION_ENDPOINTS: EndpointDoc[] = [
  ...KEYED_ENDPOINTS,
  ...KEYED_ENDPOINTS.filter((e) => e.path.startsWith('/servers/{key}')).map(bySource),
];
